import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Edit,
  Delete,
  Add,
  Refresh,
  Person,
  ToggleOn,
  ToggleOff,
  AdminPanelSettings,
  SupervisedUserCircle,
  Security,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchAdmins,
  toggleAdminStatus,
  deleteAdmin,
  setFilters,
  clearFilters,
  setPage,
} from '../store/slices/adminsSlice';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import AdminFormModal from '../components/admins/AdminFormModal';
import { AdminData } from '../types';

const AdminsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { admins, pagination, loading, error } = useSelector(
    (state: RootState) => state.admins
  );
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canSeeAllAdmins =
    currentUser?.role === 'owner' || currentUser?.role === 'superadmin';
  const isPlainAdmin = currentUser?.role === 'admin';
  const canManageAdmins = canSeeAllAdmins;

  // Only strip elevated accounts when we know the viewer is a plain admin.
  // If role is unknown, trust the API response (server already filters).
  const visibleAdmins = isPlainAdmin
    ? admins.filter((a) => a.role === 'admin')
    : admins;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminData | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchAdmins({ page: 1, limit: 20 }) as any);
  }, [dispatch]);

  useEffect(() => {
    // Clear elevated role filter if current user cannot see those roles
    if (isPlainAdmin && (roleFilter === 'owner' || roleFilter === 'superadmin')) {
      setRoleFilter('');
    }
  }, [isPlainAdmin, roleFilter]);

  const handleSearch = () => {
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (roleFilter) filters.role = roleFilter;
    if (statusFilter) filters.isActive = statusFilter === 'active';
    dispatch(setFilters(filters));
    dispatch(fetchAdmins({ page: 1, limit: 20, filters }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(fetchAdmins({ page: newPage + 1, limit: 20 }) as any);
  };

  const handleRefresh = () => {
    dispatch(fetchAdmins({ page: 1, limit: 20 }) as any);
  };

  const handleCreate = () => {
    setFormMode('create');
    setSelectedAdmin(null);
    setShowFormModal(true);
  };

  const handleEdit = (admin: AdminData) => {
    setFormMode('edit');
    setSelectedAdmin(admin);
    setShowFormModal(true);
  };

  const handleToggleStatus = async (admin: AdminData) => {
    try {
      await dispatch(toggleAdminStatus(admin.id) as any);
      toast.success(`Admin ${admin.isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      toast.error('Failed to toggle admin status');
    }
  };

  const handleDelete = async () => {
    if (!selectedAdmin) return;
    
    setDeleteLoading(true);
    try {
      await dispatch(deleteAdmin(selectedAdmin.id) as any);
      toast.success('Admin deleted successfully');
      setShowDeleteDialog(false);
      setSelectedAdmin(null);
    } catch (error) {
      toast.error('Failed to delete admin');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getRoleLabel = (role: string) => {
    const roleMap: { [key: string]: string } = {
      owner: 'Owner',
      superadmin: 'Super Admin',
      admin: 'Admin',
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role: string) => {
    const colorMap: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'error' } = {
      owner: 'error',
      superadmin: 'warning',
      admin: 'primary',
    };
    return colorMap[role] || 'default';
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Security />;
      case 'superadmin':
        return <SupervisedUserCircle />;
      case 'admin':
        return <AdminPanelSettings />;
      default:
        return <Person />;
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Admins
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={handleRefresh}
          disabled={loading}
          size="small"
          sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        <Card>
          <CardContent sx={{ py: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Person sx={{ mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">{visibleAdmins.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Admins
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent sx={{ py: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ToggleOn sx={{ mr: 2, color: 'success.main' }} />
              <Box>
                <Typography variant="h6">
                  {visibleAdmins.filter(a => a.isActive).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Admins
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent sx={{ py: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ToggleOff sx={{ mr: 2, color: 'error.main' }} />
              <Box>
                <Typography variant="h6">
                  {visibleAdmins.filter(a => !a.isActive).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inactive Admins
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, mb: 2 }}>
          <TextField
            placeholder="Search admins..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
            size="small"
          />
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button variant="contained" onClick={handleSearch} size="small" sx={{ flex: { xs: 1, sm: 'none' } }}>
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setStatusFilter('');
                  dispatch(clearFilters());
                  dispatch(fetchAdmins({ page: 1, limit: 20 }) as any);
                }}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Clear
              </Button>
            </Box>
            
            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Filters
              </Button>
              {canManageAdmins && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleCreate}
                  size="small"
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Add Admin
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        {showFilters && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                label="Role"
              >
                <MenuItem value="">All Roles</MenuItem>
                {canSeeAllAdmins && <MenuItem value="owner">Owner</MenuItem>}
                {canSeeAllAdmins && <MenuItem value="superadmin">Super Admin</MenuItem>}
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Admins Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table sx={{ minWidth: { xs: 520, sm: 700, md: 800 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Last Login</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                {canManageAdmins && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canManageAdmins ? 8 : 7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : visibleAdmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageAdmins ? 8 : 7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No admins found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleAdmins.map((admin) => (
                  <TableRow key={admin.id} hover>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body1" fontWeight="medium">
                        {admin.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          {admin.email}
                        </Typography>
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="textSecondary">
                            {admin.fullName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {admin.phoneNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="body2">
                        {admin.phoneNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          icon={getRoleIcon(admin.role)}
                          label={getRoleLabel(admin.role)}
                          color={getRoleColor(admin.role)}
                          size="small"
                        />
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="textSecondary">
                            {admin.lastLogin ? dayjs(admin.lastLogin).format('MMM DD, HH:mm') : 'Never'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={admin.isActive ? 'Active' : 'Inactive'}
                          color={admin.isActive ? 'success' : 'default'}
                          size="small"
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {admin.createdAt ? dayjs(admin.createdAt).format('MMM DD, YYYY') : '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Typography variant="body2">
                        {admin.lastLogin ? dayjs(admin.lastLogin).format('MMM DD, YYYY HH:mm') : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2">
                        {admin.createdAt ? dayjs(admin.createdAt).format('MMM DD, YYYY') : '-'}
                      </Typography>
                    </TableCell>
                    {canManageAdmins && (
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Tooltip title="Toggle Status">
                            <IconButton
                              size="small"
                              onClick={() => handleToggleStatus(admin)}
                              color={admin.isActive ? 'success' : 'default'}
                            >
                              {admin.isActive ? <ToggleOn /> : <ToggleOff />}
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(admin)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedAdmin(admin);
                                setShowDeleteDialog(true);
                              }}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={(e) => {
            const newLimit = parseInt(e.target.value, 10);
            dispatch(fetchAdmins({ page: 1, limit: newLimit }) as any);
          }}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Form Modal */}
      <AdminFormModal
        open={showFormModal}
        onClose={() => setShowFormModal(false)}
        admin={selectedAdmin}
        mode={formMode}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Admin</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete "{selectedAdmin?.fullName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminsPage; 